// src/components/Pentests/tabs/Findings.js
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Eye,
  EyeOff,
  FlaskConical,
  Loader2,
  MinusCircle,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  Radio,
  ShieldOff,
  Sparkles,
  Trash2,
  Undo2,
  XCircle,
} from 'lucide-react';
import { beaconsAPI, pentestsAPI } from '../../../services/api';
import KaliTaskOutputViewer from '../../common/KaliTaskOutputViewer';
import { useAuth } from '../../../contexts/AuthContext';
import { PrimaryCTA, EmptyState } from '../../common/Shinobi';
import ConfirmModal from '../../common/ConfirmModal';
import FindingForm from './FindingForm';
import FalsePositiveModal from './FalsePositiveModal';
import { parseServerDate } from '../../../utils/time';

// Worst → best. Used to sort the Findings table by severity column.
const SEVERITY_RANK = { critical: 5, high: 4, medium: 3, low: 2, info: 1 };

const SEVERITY_BADGE = {
  info: 'bg-slate-100 text-slate-700',
  low: 'bg-blue-100 text-blue-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const VERIFICATION_OPTIONS = [
  { value: 'still_open', label: 'Still Open',  icon: XCircle,      cls: 'bg-rose-100 text-rose-700 border-rose-300' },
  { value: 'fixed',      label: 'Fixed',       icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'wont_fix',   label: "Won't Fix",   icon: MinusCircle,  cls: 'bg-amber-100 text-amber-700 border-amber-300' },
];

const VERIFICATION_BY_VALUE = Object.fromEntries(VERIFICATION_OPTIONS.map((o) => [o.value, o]));

function AutoBadge({ finding }) {
  if (!finding || finding.operator_modified) return null;
  if (finding.source === 'auto_extracted') {
    const scanner = finding.auto_origin?.scanner || 'scanner';
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-cyan-100 text-cyan-700 border-cyan-300 shrink-0 mt-0.5 cursor-help"
        title={`Auto-extracted from ${scanner}. Edit this finding to take ownership; the badge disappears once you save your changes.`}
      >
        <Bot className="w-3 h-3" />
        Auto
      </span>
    );
  }
  if (finding.source === 'agentic') {
    const o = finding.auto_origin || {};
    const sep = o.strongest_separation == null ? 'n/a' : Number(o.strongest_separation).toFixed(2);
    const control = { true: 'passed', false: 'FAILED', null: 'not run', undefined: 'not run' }[
      String(o.negative_control_passed)
    ] || 'not run';
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-wasabi-100 text-wasabi-800 border-wasabi-300 shrink-0 mt-0.5 cursor-help"
        title={
          `Claimed by ${o.codename || 'an agent'} over ${o.turns ?? '?'} turns, then attacked by an `
          + 'independent reviewer that saw the evidence without the argument.\n'
          + `Strongest oracle separation ${sep}, negative control ${control}.`
          + (o.refuter_explanation ? `\n\nReviewer: ${o.refuter_explanation}` : '')
        }
      >
        <Bot className="w-3 h-3" />
        Agent
      </span>
    );
  }
  if (finding.source === 'ai_extracted') {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-purple-100 text-purple-700 border-purple-300 shrink-0 mt-0.5 cursor-help"
        title="AI-extracted from raw scanner output. Verify against the source step before treating this as confirmed."
      >
        <Sparkles className="w-3 h-3" />
        AI
      </span>
    );
  }
  return null;
}

/**
 * Believed, and not counted yet.
 *
 * Deliberately not styled like the FP badge even though both mean "excluded
 * from the score", because they are opposite claims: a false positive is not
 * real, and this is real pending the operator's call. Amber rather than grey,
 * and the word is "provisional" rather than anything that reads as doubt about
 * the evidence.
 */
function ProvisionalBadge({ finding }) {
  if (!finding?.provisional) return null;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-amber-100 text-amber-800 border-amber-300 shrink-0 mt-0.5 cursor-help"
      title={
        'Provisional: this survived review but you have not accepted it, so it does not '
        + 'count toward the severity totals or the grade. Accept it to let it count.'
      }
    >
      <Clock className="w-3 h-3" />
      Provisional
    </span>
  );
}

function FalsePositiveBadge({ finding }) {
  if (!finding?.false_positive) return null;
  const when = parseServerDate(finding.false_positive_marked_at);
  const tip = `False positive: ${finding.false_positive_reason || '(no reason given)'}\nMarked ${when ? when.toLocaleString() : ''}`;
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-slate-600/30 text-slate-700 border-slate-300 shrink-0 mt-0.5 cursor-help"
      title={tip}
    >
      <ShieldOff className="w-3 h-3" />
      FP
    </span>
  );
}

function VerificationPill({ status, onChange, disabled }) {
  const opt = VERIFICATION_BY_VALUE[status] || VERIFICATION_OPTIONS[0];
  const Icon = opt.icon;
  if (disabled) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${opt.cls}`}>
        <Icon className="w-3 h-3" />
        {opt.label}
      </span>
    );
  }
  return (
    <select
      value={status || 'still_open'}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(e.target.value)}
      className={`inline-block text-[10px] font-bold uppercase tracking-wider rounded border px-2 py-0.5 outline-none ${opt.cls} bg-cyber-50`}
    >
      {VERIFICATION_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export default function Findings({ onUpdated }) {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const { isPentester } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, finding: null });
  const [deleting, setDeleting] = useState(false);
  const [hideFP, setHideFP] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all'); // all | still_open | fixed | wont_fix | new
  // 'desc' = Critical first (default), 'asc' = Info first. Click the Severity
  // header to toggle between the two.
  const [severitySort, setSeveritySort] = useState('desc');
  const [fpModal, setFpModal] = useState({ open: false, finding: null });
  const [fpSubmitting, setFpSubmitting] = useState(false);

  // Phase 1 (2026-05-14): per-finding AI remediation lives in the row
  // expansion. The cache is bulk-populated from /ai-insights on mount and
  // updated incrementally when the operator generates / regenerates a single
  // finding's analysis.
  const [expanded, setExpanded] = useState(() => new Set());
  const [aiAnalyses, setAiAnalyses] = useState(() => new Map());
  const [aiAvailable, setAiAvailable] = useState(false);
  const [generatingForFinding, setGeneratingForFinding] = useState(null);
  const rowRefs = useRef(new Map());

  const fetchList = useCallback(async () => {
    try {
      const data = await pentestsAPI.findings.list(pentestId);
      setFindings(data || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  const fetchAIAnalyses = useCallback(async () => {
    try {
      const insights = await pentestsAPI.aiInsights.get(pentestId);
      setAiAvailable(!!insights?.available);
      const next = new Map();
      (insights?.finding_analyses || []).forEach((a) => {
        if (a.pentest_finding_id != null) next.set(a.pentest_finding_id, a);
      });
      setAiAnalyses(next);
    } catch {
      // Findings tab still functions without the AI cache; silently skip.
    }
  }, [pentestId]);

  useEffect(() => {
    fetchList();
    fetchAIAnalyses();
  }, [fetchList, fetchAIAnalyses]);

  // Honour ?focus=<findingId> from the AI Insights deep-links: auto-expand
  // and scroll the row into view, then strip the param so a refresh does
  // not keep re-firing the scroll.
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (!focus || findings.length === 0) return;
    const fid = Number(focus);
    if (Number.isNaN(fid)) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(fid);
      return next;
    });
    requestAnimationFrame(() => {
      const node = rowRefs.current.get(fid);
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const params = new URLSearchParams(searchParams);
    params.delete('focus');
    setSearchParams(params, { replace: true });
  }, [findings, searchParams, setSearchParams]);

  const toggleExpand = useCallback((fid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(fid)) next.delete(fid); else next.add(fid);
      return next;
    });
  }, []);

  const generateRemediation = useCallback(async (fid) => {
    setGeneratingForFinding(fid);
    setError(null);
    try {
      const fresh = await pentestsAPI.aiInsights.analyzeFinding(pentestId, fid);
      setAiAnalyses((prev) => {
        const next = new Map(prev);
        next.set(fid, fresh);
        return next;
      });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setGeneratingForFinding(null);
    }
  }, [pentestId]);

  const fpCount = useMemo(() => findings.filter((f) => f.false_positive).length, [findings]);

  // A PT is a "retest" from the findings tab's POV when at least one finding
  // was cloned from a parent finding -- which is exactly the create_pentest_retest
  // path. Newly auto-extracted findings during the retest have parent_finding_id
  // = null and are surfaced as "new in this retest".
  const isRetest = useMemo(() => findings.some((f) => f.parent_finding_id != null), [findings]);

  // Verification tally over non-FP findings; mirrors the backend
  // VerificationProgress shape so the header counter reads the same numbers
  // as the API. NULL verification_status counts as still_open.
  const verCounts = useMemo(() => {
    const tally = { verified: 0, still_open: 0, wont_fix: 0, new: 0 };
    for (const f of findings) {
      if (f.false_positive) continue;
      if (!f.parent_finding_id) tally.new += 1;
      const s = f.verification_status;
      if (s === 'fixed') tally.verified += 1;
      else if (s === 'wont_fix') tally.wont_fix += 1;
      else tally.still_open += 1;
    }
    return tally;
  }, [findings]);

  const visible = useMemo(() => {
    let xs = hideFP ? findings.filter((f) => !f.false_positive) : findings;
    if (statusFilter === 'still_open') {
      xs = xs.filter((f) => !f.verification_status || f.verification_status === 'still_open');
    } else if (statusFilter === 'fixed') {
      xs = xs.filter((f) => f.verification_status === 'fixed');
    } else if (statusFilter === 'wont_fix') {
      xs = xs.filter((f) => f.verification_status === 'wont_fix');
    } else if (statusFilter === 'new') {
      xs = xs.filter((f) => !f.parent_finding_id);
    }
    if (severitySort) {
      const dir = severitySort === 'desc' ? -1 : 1;
      xs = [...xs].sort((a, b) => {
        const ra = SEVERITY_RANK[a.severity] || 0;
        const rb = SEVERITY_RANK[b.severity] || 0;
        if (ra !== rb) return (ra - rb) * dir;
        // Tiebreak by reference number for a stable order within a severity.
        return (a.reference_number || 0) - (b.reference_number || 0);
      });
    }
    return xs;
  }, [findings, hideFP, statusFilter, severitySort]);

  const toggleSeveritySort = () => {
    setSeveritySort((s) => (s === 'desc' ? 'asc' : 'desc'));
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const f = await pentestsAPI.findings.get(pentestId, id);
      setEditing(f);
      setFormOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const onSubmit = async (payload) => {
    setSubmitting(true);
    try {
      const envelope = editing
        ? await pentestsAPI.findings.update(pentestId, editing.id, payload)
        : await pentestsAPI.findings.create(pentestId, payload);
      if (envelope?.pentest && onUpdated) onUpdated(envelope.pentest);
      setFormOpen(false);
      setEditing(null);
      await fetchList();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onVerificationChange = async (finding, newStatus) => {
    setError(null);
    try {
      const envelope = await pentestsAPI.findings.update(pentestId, finding.id, {
        verification_status: newStatus,
      });
      if (envelope?.pentest && onUpdated) onUpdated(envelope.pentest);
      await fetchList();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const onDeleteConfirm = async () => {
    if (!confirmDelete.finding) return;
    setDeleting(true);
    try {
      const envelope = await pentestsAPI.findings.remove(pentestId, confirmDelete.finding.id);
      if (envelope?.pentest && onUpdated) onUpdated(envelope.pentest);
      setConfirmDelete({ isOpen: false, finding: null });
      await fetchList();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setDeleting(false);
    }
  };

  // Mirrors the backend _normalize_fingerprint_for_grouping: split scanner at
  // the first ':', then within each '|'-segment collapse any URL down to
  // <scheme>://<host>. So `zap:10020|https://x/foo|param` and
  // `zap:10020|https://x/bar|param` both normalize to
  // `zap:10020|https://x|param` and the operator can bulk-suppress the
  // "everything after / returns 200" false-positive class with one click.
  const normalizeFp = (fp) => {
    if (!fp) return null;
    const colonIdx = fp.indexOf(':');
    if (colonIdx < 0) return null;
    const scanner = fp.slice(0, colonIdx);
    const rest = fp.slice(colonIdx + 1);
    if (!rest) return null;
    const segs = rest.split('|').map((seg) => {
      if (seg.startsWith('http://') || seg.startsWith('https://')) {
        try {
          const u = new URL(seg);
          return u.host ? `${u.protocol}//${u.host}` : seg;
        } catch { return seg; }
      }
      return seg;
    });
    return `${scanner}:${segs.join('|')}`;
  };

  const similarOpenCount = (finding) => {
    if (!finding || finding.source === 'manual') return 0;
    const target = normalizeFp(finding.auto_fingerprint);
    if (!target) return 0;
    return findings.filter((f) =>
      f.id !== finding.id
      && !f.false_positive
      && f.source !== 'manual'
      && normalizeFp(f.auto_fingerprint) === target,
    ).length;
  };

  const onMarkFP = async ({ reason, suppressSimilar }) => {
    if (!fpModal.finding) return;
    setFpSubmitting(true);
    setError(null);
    try {
      const envelope = await pentestsAPI.findings.markFalsePositive(pentestId, fpModal.finding.id, {
        reason,
        suppress_similar: suppressSimilar,
      });
      if (envelope?.pentest && onUpdated) onUpdated(envelope.pentest);
      setFpModal({ open: false, finding: null });
      await fetchList();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setFpSubmitting(false);
    }
  };

  /**
   * Accept a provisional finding into the score.
   *
   * The operator's half of the agentic loop. A claim that survived the refuter
   * is evidence and not a judgement, so it lands visible and uncounted; this is
   * where somebody decides it counts. The response carries the recomputed
   * pentest, so the grade on screen is the one the click produced rather than
   * the one from before it.
   */
  const onAcceptFinding = async (finding) => {
    setError(null);
    try {
      const envelope = await pentestsAPI.findings.accept(pentestId, finding.id);
      if (envelope?.pentest && onUpdated) onUpdated(envelope.pentest);
      await fetchList();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  const onUnmarkFP = async (finding) => {
    setError(null);
    try {
      const envelope = await pentestsAPI.findings.unmarkFalsePositive(pentestId, finding.id);
      if (envelope?.pentest && onUpdated) onUpdated(envelope.pentest);
      await fetchList();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    }
  };

  if (loading) return <div className="text-slate-500">Loading findings...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-slate-900">
            Findings
            <span className="ml-2 text-xs text-slate-500 font-normal">
              {visible.length} shown
              {fpCount > 0 && ` / ${fpCount} false positive${fpCount === 1 ? '' : 's'}`}
            </span>
          </h2>
          {isRetest && (
            <div className="flex items-center gap-3 text-xs">
              <span className="text-emerald-700">
                <CheckCircle2 className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                {verCounts.verified} verified
              </span>
              <span className="text-rose-700">
                <XCircle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                {verCounts.still_open} open
              </span>
              <span className="text-amber-700">
                <MinusCircle className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                {verCounts.wont_fix} won't fix
              </span>
              {verCounts.new > 0 && (
                <span className="text-cyan-700">
                  <Sparkles className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                  {verCounts.new} new in this retest
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isRetest && (
            <div className="inline-flex rounded-lg border border-cyber-300/60 bg-cyber-100/60 overflow-hidden text-xs">
              {[
                { value: 'all',         label: 'All',          count: findings.filter(f => !f.false_positive).length },
                { value: 'still_open',  label: 'Still Open',   count: verCounts.still_open },
                { value: 'fixed',       label: 'Fixed',        count: verCounts.verified },
                { value: 'wont_fix',    label: "Won't Fix",    count: verCounts.wont_fix },
                { value: 'new',         label: 'New',          count: verCounts.new },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`px-2.5 py-1.5 transition border-r last:border-r-0 border-cyber-300/60 ${
                    statusFilter === opt.value
                      ? 'bg-wasabi-100 text-wasabi-700'
                      : 'text-slate-700 hover:bg-cyber-200/60'
                  }`}
                  title={`Filter to ${opt.label.toLowerCase()} findings`}
                >
                  {opt.label} <span className="ml-1 opacity-70">({opt.count})</span>
                </button>
              ))}
            </div>
          )}
          {fpCount > 0 && (
            <button
              type="button"
              onClick={() => setHideFP((h) => !h)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition ${
                hideFP
                  ? 'bg-cyber-100 border-cyber-300/60 text-slate-700 hover:border-cyber-300'
                  : 'bg-wasabi-100 border-wasabi-300 text-shinobi-green'
              }`}
              title={hideFP ? `${fpCount} false positives are hidden. Click to show.` : 'Hide false positives'}
            >
              {hideFP ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {hideFP ? `Show FPs (${fpCount})` : `Hide FPs (${fpCount})`}
            </button>
          )}
          {isPentester && (
            <PrimaryCTA variant="green" icon={Plus} iconPosition="left" onClick={openCreate}>
              Add Finding
            </PrimaryCTA>
          )}
        </div>
      </div>

      {error && <div className="text-red-600 text-sm">{error}</div>}

      {findings.length === 0 ? (
        <EmptyState
          icon={Plus}
          accent="green"
          title="No findings yet"
          description={isPentester ? 'Manually create the first one.' : 'Once the Pentester adds findings, they will show up here.'}
          actionLabel={isPentester ? 'Add Finding' : null}
          actionIcon={Plus}
          onAction={isPentester ? openCreate : null}
        />
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-cyber-300/40 bg-cyber-50/60 p-6 text-center text-sm text-slate-500">
          {statusFilter !== 'all' ? (
            <>
              No findings match the <span className="text-shinobi-green font-medium">{statusFilter.replace('_', ' ')}</span> filter.{' '}
              <button type="button" onClick={() => setStatusFilter('all')} className="text-shinobi-green hover:underline">Clear filter</button>
            </>
          ) : (
            <>
              All {fpCount} {fpCount === 1 ? 'finding is' : 'findings are'} marked as false positive. Click <span className="text-shinobi-green font-medium">Show FPs</span> above to review.
            </>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
          <table className="w-full text-sm text-left text-slate-800">
            <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
              <tr>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">
                  <button
                    type="button"
                    onClick={toggleSeveritySort}
                    className="inline-flex items-center gap-1 uppercase tracking-wider text-shinobi-green hover:opacity-80 transition"
                    title={
                      severitySort === 'desc'
                        ? 'Sorted Critical → Info. Click to flip to Info → Critical.'
                        : 'Sorted Info → Critical. Click to flip to Critical → Info.'
                    }
                  >
                    Severity
                    {severitySort === 'desc' ? (
                      <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronUp className="w-3.5 h-3.5" />
                    )}
                  </button>
                </th>
                <th className="px-4 py-3">Verification</th>
                <th className="px-4 py-3">CWE</th>
                <th className="px-4 py-3">CVSS</th>
                <th className="px-4 py-3">Complexity</th>
                <th className="px-4 py-3">Priority</th>
                {isPentester && <th className="px-4 py-3 w-32"></th>}
              </tr>
            </thead>
            <tbody>
              {visible.map((f) => {
                const isFP = !!f.false_positive;
                const rowCls = isFP
                  ? 'border-t border-cyber-300/30 bg-cyber-100/30 opacity-60 hover:opacity-100 transition'
                  : 'border-t border-cyber-300/30 hover:bg-cyber-200/40';
                const titleCls = isFP ? 'flex-1 line-through' : 'flex-1';
                const isExpanded = expanded.has(f.id);
                const colSpan = isPentester ? 9 : 8;
                return (
                  <Fragment key={f.id}>
                    <tr
                      ref={(el) => {
                        if (el) rowRefs.current.set(f.id, el);
                        else rowRefs.current.delete(f.id);
                      }}
                      className={rowCls}
                    >
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">#{f.reference_number}</td>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-start gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => toggleExpand(f.id)}
                          title={isExpanded ? 'Collapse' : 'Expand for AI remediation'}
                          aria-label={isExpanded ? 'Collapse finding' : 'Expand finding'}
                          className="shrink-0 p-0.5 rounded text-slate-500 hover:text-shinobi-green hover:bg-wasabi-100 transition mt-0.5"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        <FalsePositiveBadge finding={f} />
                        <ProvisionalBadge finding={f} />
                        <AutoBadge finding={f} />
                        {isRetest && !f.parent_finding_id && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-cyan-100 text-cyan-700 border-cyan-300 shrink-0 mt-0.5"
                            title="Discovered during this retest -- not present in the original engagement."
                          >
                            <Sparkles className="w-3 h-3" />
                            New
                          </span>
                        )}
                        <span className={titleCls}>{f.title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs rounded ${SEVERITY_BADGE[f.severity] || ''} ${isFP ? 'opacity-60' : ''}`}>
                        {f.severity}
                        {f.severity_manual && <span className="ml-1 text-[10px] opacity-70">(manual)</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {f.parent_finding_id ? (
                        <VerificationPill
                          status={f.verification_status || 'still_open'}
                          disabled={!isPentester || isFP}
                          onChange={(newStatus) => onVerificationChange(f, newStatus)}
                        />
                      ) : (
                        <span className="text-xs text-slate-600">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{(f.cwe_ids || []).join(', ') || '-'}</td>
                    <td className="px-4 py-3 tabular-nums">{f.cvss_score ?? '-'}</td>
                    <td className="px-4 py-3 text-slate-700">{f.complexity || '-'}</td>
                    <td className="px-4 py-3 tabular-nums">{f.priority ?? '-'}</td>
                    {isPentester && (
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {/* Accept comes first because on a provisional row it
                              is the decision being asked for. Hidden once the
                              finding counts, so it is never a control that does
                              nothing. */}
                          {f.provisional && !isFP && (
                            <button
                              type="button"
                              onClick={() => onAcceptFinding(f)}
                              title="Accept this finding: it starts counting toward the severity totals and the grade"
                              className="inline-flex items-center gap-1 rounded border border-wasabi-300 bg-wasabi-50 px-2 py-1 text-[11px] font-semibold text-wasabi-800 hover:bg-wasabi-100"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Accept
                            </button>
                          )}
                          {isFP ? (
                            <button
                              type="button"
                              onClick={() => onUnmarkFP(f)}
                              title="Restore - un-mark as false positive"
                              className="p-1.5 rounded text-slate-500 hover:text-cyan-700 hover:bg-cyan-100"
                            >
                              <Undo2 className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setFpModal({ open: true, finding: f })}
                              title="Mark as false positive"
                              className="p-1.5 rounded text-slate-500 hover:text-amber-700 hover:bg-amber-100"
                            >
                              <ShieldOff className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => openEdit(f.id)}
                            title="Edit finding"
                            className="p-1.5 rounded text-slate-500 hover:text-shinobi-green hover:bg-wasabi-100"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete({ isOpen: true, finding: f })}
                            title="Delete finding"
                            className="p-1.5 rounded text-slate-500 hover:text-red-600 hover:bg-red-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {isExpanded && (
                    <tr className="border-t border-cyber-300/20 bg-cyber-50/40">
                      <td colSpan={colSpan} className="px-4 py-4">
                        <FindingExpansion
                          pentestId={pentestId}
                          finding={f}
                          analysis={aiAnalyses.get(f.id)}
                          aiAvailable={aiAvailable}
                          isPentester={isPentester}
                          generating={generatingForFinding === f.id}
                          onGenerate={() => generateRemediation(f.id)}
                          onFindingChange={async (envFinding) => {
                            if (envFinding && onUpdated) {
                              // refresh the parent PT row + the findings list
                              await fetchList();
                            }
                          }}
                        />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {formOpen && (
        <FindingForm
          initial={editing}
          submitting={submitting}
          onCancel={() => { setFormOpen(false); setEditing(null); }}
          onSubmit={onSubmit}
        />
      )}

      <FalsePositiveModal
        isOpen={fpModal.open}
        finding={fpModal.finding}
        similarCount={similarOpenCount(fpModal.finding)}
        submitting={fpSubmitting}
        onClose={() => !fpSubmitting && setFpModal({ open: false, finding: null })}
        onSubmit={onMarkFP}
      />

      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => !deleting && setConfirmDelete({ isOpen: false, finding: null })}
        onConfirm={onDeleteConfirm}
        title="Delete Finding"
        message={
          confirmDelete.finding
            ? `Permanently delete finding #${confirmDelete.finding.reference_number}: "${confirmDelete.finding.title}"? This cannot be undone.`
            : ''
        }
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}


function FindingExpansion({ pentestId, finding, analysis, aiAvailable, isPentester, generating, onGenerate, onFindingChange }) {
  const result = analysis?.result || {};
  const hasAnalysis = !!analysis;

  return (
    <div className="rounded-xl border border-cyber-300/40 bg-cyber-100/40 p-4 space-y-4">
      <AiTestCard
        pentestId={pentestId}
        finding={finding}
        aiAvailable={aiAvailable}
        isPentester={isPentester}
        onFindingChange={onFindingChange}
      />

      {finding.scope_description && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">
            Scope description
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
            {finding.scope_description}
          </p>
        </section>
      )}

      {finding.recommendation && (
        <section>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">
            Operator recommendation
          </div>
          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
            {finding.recommendation}
          </p>
        </section>
      )}

      <section className="rounded-lg border border-wasabi-300 bg-shinobi-green/5 p-3">
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-shinobi-green" />
            <span className="text-sm font-semibold text-slate-900">Wasabi Shinobi AI remediation</span>
            {hasAnalysis && (
              <span className="text-[11px] text-slate-500">
                {analysis.duration_seconds ? `(${analysis.duration_seconds.toFixed(1)}s)` : ''}
                {analysis.model_used ? ` via ${analysis.model_used}` : ''}
              </span>
            )}
          </div>
          {isPentester && aiAvailable && (
            <button
              type="button"
              onClick={onGenerate}
              disabled={generating}
              className="inline-flex items-center gap-1.5 text-xs text-shinobi-green hover:underline disabled:opacity-50"
            >
              {generating ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  {hasAnalysis ? 'Regenerating...' : 'Generating...'}
                </>
              ) : (
                <>
                  <RefreshCw className="w-3 h-3" />
                  {hasAnalysis ? 'Regenerate' : 'Generate'}
                </>
              )}
            </button>
          )}
        </div>

        {!aiAvailable && !hasAnalysis && (
          <div className="flex items-start gap-2 text-xs text-amber-700/80">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>AI service is not configured on this server.</span>
          </div>
        )}

        {generating && !hasAnalysis && (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin text-shinobi-green" />
            Wasabi Shinobi AI is drafting remediation steps for this finding.
          </div>
        )}

        {hasAnalysis ? (
          <div className="space-y-3 text-sm text-slate-800 leading-relaxed">
            {result.overview && <p className="text-slate-900">{result.overview}</p>}

            {Array.isArray(result.remediation_steps) && result.remediation_steps.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">
                  Remediation steps
                </div>
                <ol className="space-y-1.5 list-none">
                  {result.remediation_steps.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-shinobi-green font-mono text-xs mt-0.5 shrink-0">{i + 1}.</span>
                      <span className="text-slate-700">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {Array.isArray(result.references) && result.references.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1 flex items-center gap-1.5">
                  <BookOpen className="w-3 h-3" /> References
                </div>
                <ul className="space-y-1">
                  {result.references.map((r, i) => (
                    <li key={i} className="text-xs font-mono text-slate-500">{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.estimated_effort && (
              <div className="pt-1 flex items-center gap-2 text-xs text-slate-500">
                <span className="uppercase tracking-wider text-slate-500">Effort</span>
                <span className="font-mono text-slate-700">{result.estimated_effort}</span>
              </div>
            )}
          </div>
        ) : !generating && aiAvailable ? (
          <div className="text-xs text-slate-500">
            {isPentester ? (
              <>Click <span className="text-shinobi-green">Generate</span> to draft remediation steps from this finding.</>
            ) : (
              'Pentester can generate remediation steps for this finding.'
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
}


function AiTestCard({ pentestId, finding, aiAvailable, isPentester, onFindingChange }) {
  // Fetches the latest persisted test_command lazily on mount; Generate
  // / Regenerate hits the POST endpoint; Run-on-beacon picks a Pen Tester
  // beacon and dispatches a kali_command via the v2 run-test endpoint.
  const [test, setTest] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  const [beaconPickerOpen, setBeaconPickerOpen] = useState(false);
  const [beacons, setBeacons] = useState(null);  // null = not yet loaded
  const [dispatchingBeacon, setDispatchingBeacon] = useState(null);
  const [lastRun, setLastRun] = useState(null);  // {beacon_task_id, beacon_id, binary, args}
  const [copied, setCopied] = useState(false);

  // Phase 3 (rigor): the auto-verify summariser fires when a Run-on-beacon
  // task completes. We poll for it after a dispatch, and on mount we fetch
  // any pre-existing verdict from prior runs.
  const [verification, setVerification] = useState(null);
  const [verificationApplying, setVerificationApplying] = useState(false);
  const verifyPollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    pentestsAPI.aiInsights.getTestCommand(pentestId, finding.id)
      .then((data) => {
        if (cancelled) return;
        setTest(data);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    pentestsAPI.aiInsights.getTestVerification(pentestId, finding.id)
      .then((data) => { if (!cancelled && data) setVerification(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pentestId, finding.id]);

  // After a Run-on-beacon dispatch, poll the verification endpoint for ~3min.
  // The summariser fires from a FastAPI BackgroundTask once the beacon
  // reports task completion, so the row may take 5-90s to appear.
  useEffect(() => {
    if (!lastRun?.beacon_task_id) return;
    const startedAt = Date.now();
    const tick = async () => {
      try {
        const data = await pentestsAPI.aiInsights.getTestVerification(pentestId, finding.id);
        if (data && (!verification || data.id !== verification.id)) {
          setVerification(data);
          if (verifyPollRef.current) { clearInterval(verifyPollRef.current); verifyPollRef.current = null; }
        }
      } catch {}
      if (Date.now() - startedAt > 3 * 60 * 1000) {
        if (verifyPollRef.current) { clearInterval(verifyPollRef.current); verifyPollRef.current = null; }
      }
    };
    verifyPollRef.current = setInterval(tick, 6000);
    return () => {
      if (verifyPollRef.current) { clearInterval(verifyPollRef.current); verifyPollRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRun?.beacon_task_id, pentestId, finding.id]);

  // Operator-driven flips: confirmed -> still_open (it's real), refuted -> FP.
  const onApplyConfirmed = async () => {
    setVerificationApplying(true);
    try {
      const envelope = await pentestsAPI.findings.update(pentestId, finding.id, {
        verification_status: 'still_open',
      });
      if (onFindingChange && envelope?.finding) onFindingChange(envelope.finding);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setVerificationApplying(false);
    }
  };
  const onApplyRefuted = async () => {
    setVerificationApplying(true);
    try {
      const reason = `AI-verified false positive: ${verification?.result?.summary || 'test output refuted the finding'}`;
      const envelope = await pentestsAPI.findings.markFalsePositive(pentestId, finding.id, {
        reason,
        suppress_similar: false,
      });
      if (onFindingChange && envelope?.finding) onFindingChange(envelope.finding);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setVerificationApplying(false);
    }
  };

  const result = test?.result || {};
  const commandPreview = useMemo(() => {
    const r = test?.result || {};
    if (!r.binary) return '';
    const args = Array.isArray(r.args) ? r.args : [];
    return [r.binary, ...args].map((s) => {
      const str = String(s);
      return /[\s"']/.test(str) ? `'${str.replace(/'/g, "'\\''")}'` : str;
    }).join(' ');
  }, [test]);

  const onGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const fresh = await pentestsAPI.aiInsights.generateTestCommand(pentestId, finding.id);
      setTest(fresh);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const onCopy = async () => {
    if (!commandPreview) return;
    try {
      await navigator.clipboard.writeText(commandPreview);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const onOpenBeaconPicker = async () => {
    setBeaconPickerOpen((v) => !v);
    if (beacons === null) {
      try {
        // beaconsAPI.list returns the raw axios response; the v2 endpoint
        // wraps the rows under `beacons` ({beacons: [...], total: N}).
        const resp = await beaconsAPI.list(0, 50);
        const body = resp?.data ?? resp;
        const rows = body?.beacons || body?.items || (Array.isArray(body) ? body : []);
        setBeacons(rows);
      } catch (err) {
        setError(err?.response?.data?.detail || err.message);
        setBeacons([]);
      }
    }
  };

  const onRunOn = async (beaconId) => {
    setDispatchingBeacon(beaconId || '__any__');
    setError(null);
    try {
      const resp = await pentestsAPI.aiInsights.runTest(pentestId, finding.id, { beaconId });
      setLastRun(resp);
      setBeaconPickerOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setDispatchingBeacon(null);
    }
  };

  if (!loaded) {
    return null;  // brief skeleton; appears immediately on next render
  }

  return (
    <section className="rounded-lg border border-cyan-300 bg-cyan-500/5 p-3">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-cyan-700" />
          <span className="text-sm font-semibold text-slate-900">Wasabi Shinobi AI test</span>
          {test && (
            <span className="text-[11px] text-slate-500">
              {test.duration_seconds ? `(${test.duration_seconds.toFixed(1)}s)` : ''}
              {test.model_used ? ` via ${test.model_used}` : ''}
            </span>
          )}
        </div>
        {isPentester && aiAvailable && (
          <button
            type="button"
            onClick={onGenerate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 text-xs text-cyan-700 hover:underline disabled:opacity-50"
          >
            {generating ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                {test ? 'Regenerating...' : 'Generating...'}
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3" />
                {test ? 'Regenerate' : 'Generate'}
              </>
            )}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-2 rounded border border-red-300 bg-red-100 px-2 py-1.5 text-xs text-red-700">
          {error}
        </div>
      )}

      {verification && (
        <VerificationBadge
          verification={verification}
          isPentester={isPentester}
          applying={verificationApplying}
          alreadyFp={!!finding.false_positive}
          alreadyOpen={finding.verification_status === 'still_open'}
          onConfirm={onApplyConfirmed}
          onRefute={onApplyRefuted}
        />
      )}

      {!aiAvailable && !test && (
        <div className="flex items-start gap-2 text-xs text-amber-700/80">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>AI service is not configured on this server.</span>
        </div>
      )}

      {generating && !test && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin text-cyan-700" />
          Wasabi Shinobi AI is composing a safe verification command.
        </div>
      )}

      {!test && !generating && aiAvailable && (
        <div className="text-xs text-slate-500">
          {isPentester ? (
            <>Click <span className="text-cyan-700">Generate</span> to produce a safe, read-only command that validates this finding.</>
          ) : (
            'Pentester can generate a test command for this finding.'
          )}
        </div>
      )}

      {test && (
        <div className="space-y-3 text-sm text-slate-800 leading-relaxed">
          {result.description && <p className="text-slate-800">{result.description}</p>}

          <div className="relative">
            <pre className="text-xs font-mono text-cyan-100 whitespace-pre-wrap bg-cyber-100/70 border border-cyber-300/40 rounded-lg p-3 pr-20 break-all">
              {commandPreview}
            </pre>
            <button
              type="button"
              onClick={onCopy}
              title="Copy command"
              className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-cyan-300 bg-cyber-100/60 text-[10px] uppercase tracking-wider text-cyan-700 hover:bg-cyan-100"
            >
              <Copy className="w-3 h-3" />
              {copied ? 'copied' : 'copy'}
            </button>
          </div>

          {result.expected_signal && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">
                Expected signal
              </div>
              <p className="text-slate-700 text-xs">{result.expected_signal}</p>
            </div>
          )}

          {isPentester && (
            <div className="relative">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={onOpenBeaconPicker}
                  disabled={!!dispatchingBeacon}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-wasabi-300 bg-wasabi-100 text-xs font-medium text-shinobi-green hover:bg-wasabi-100 disabled:opacity-50"
                >
                  <Play className="w-3 h-3" />
                  Run on a beacon
                  <ChevronDown className="w-3 h-3" />
                </button>
                {lastRun && (
                  <span className="text-[11px] text-slate-500">
                    Dispatched as task <span className="font-mono text-slate-800">{lastRun.beacon_task_id}</span>
                    {' on '}
                    <span className="font-mono text-slate-800">{lastRun.beacon_id}</span>
                  </span>
                )}
              </div>

              {beaconPickerOpen && (
                <div className="absolute z-20 mt-1 left-0 min-w-[280px] max-w-md max-h-80 overflow-auto rounded-lg border border-cyber-300/60 bg-cyber-100 shadow-xl">
                  <button
                    type="button"
                    onClick={() => onRunOn(null)}
                    disabled={!!dispatchingBeacon}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-cyber-200/50 border-b border-cyber-300/40 flex items-center gap-2"
                  >
                    <Radio className="w-3.5 h-3.5 text-shinobi-green" />
                    <span className="text-slate-800">Any capable Pen Tester beacon</span>
                    {dispatchingBeacon === '__any__' && <Loader2 className="w-3 h-3 animate-spin text-shinobi-green ml-auto" />}
                  </button>
                  {beacons === null && (
                    <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Loading beacons...
                    </div>
                  )}
                  {Array.isArray(beacons) && beacons.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500">No beacons online.</div>
                  )}
                  {Array.isArray(beacons) && beacons.map((b) => {
                    const status = b.status || 'unknown';
                    const isOnline = status === 'online';
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => onRunOn(b.id)}
                        disabled={!!dispatchingBeacon || !isOnline}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-cyber-200/50 border-b last:border-b-0 border-cyber-300/30 flex items-center gap-2 disabled:opacity-50"
                      >
                        <Radio className={`w-3.5 h-3.5 ${isOnline ? 'text-shinobi-green' : 'text-slate-500'}`} />
                        <span className="font-mono text-slate-800 truncate flex-1">{b.label || b.hostname || b.id}</span>
                        <span className={`text-[10px] uppercase tracking-wider ${isOnline ? 'text-shinobi-green' : 'text-slate-500'}`}>{status}</span>
                        {dispatchingBeacon === b.id && <Loader2 className="w-3 h-3 animate-spin text-shinobi-green" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {lastRun && (
            <KaliTaskOutputViewer taskId={lastRun.beacon_task_id} defaultOpen pollMs={3000} />
          )}
        </div>
      )}
    </section>
  );
}


function VerificationBadge({ verification, isPentester, applying, alreadyFp, alreadyOpen, onConfirm, onRefute }) {
  const result = verification?.result || {};
  const verdict = (result.verdict || 'inconclusive').toLowerCase();
  const summary = result.summary || '';
  const evidence = result.evidence_snippet || '';

  const themes = {
    confirmed: {
      cls: 'border-emerald-300 bg-emerald-100 text-emerald-700',
      pillCls: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      label: 'Test confirms the finding',
      icon: CheckCircle2,
      ctaLabel: 'Mark Still Open',
      ctaCls: 'bg-rose-100 text-rose-700 border-rose-300 hover:bg-rose-500/25',
      ctaIcon: XCircle,
      ctaAction: onConfirm,
      ctaDisabled: alreadyOpen || alreadyFp,
      ctaDisabledHint: alreadyOpen ? 'Already marked still-open' : alreadyFp ? 'Currently a false positive' : null,
    },
    refuted: {
      cls: 'border-rose-300 bg-rose-100 text-rose-700',
      pillCls: 'bg-rose-100 text-rose-700 border-rose-300',
      label: 'Test refutes the finding (likely FP)',
      icon: XCircle,
      ctaLabel: 'Mark False Positive',
      ctaCls: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-500/25',
      ctaIcon: ShieldOff,
      ctaAction: onRefute,
      ctaDisabled: alreadyFp,
      ctaDisabledHint: alreadyFp ? 'Already marked false positive' : null,
    },
    inconclusive: {
      cls: 'border-slate-300 bg-slate-100 text-slate-800',
      pillCls: 'bg-slate-100 text-slate-700 border-slate-300',
      label: 'Test result inconclusive',
      icon: MinusCircle,
      ctaLabel: null,
    },
  };
  const theme = themes[verdict] || themes.inconclusive;
  const Icon = theme.icon;
  const CtaIcon = theme.ctaIcon;

  return (
    <div className={`mb-3 rounded-lg border p-2.5 ${theme.cls}`}>
      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${theme.pillCls}`}>
          <Icon className="w-3 h-3" />
          {theme.label}
        </span>
        {isPentester && theme.ctaLabel && (
          <button
            type="button"
            disabled={applying || theme.ctaDisabled}
            onClick={theme.ctaAction}
            title={theme.ctaDisabledHint || `Apply: ${theme.ctaLabel}`}
            className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium transition disabled:opacity-50 ${theme.ctaCls}`}
          >
            {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <CtaIcon className="w-3 h-3" />}
            {applying ? 'Applying...' : theme.ctaLabel}
          </button>
        )}
      </div>
      {summary && <p className="text-xs leading-relaxed text-slate-800">{summary}</p>}
      {evidence && (
        <pre className="mt-2 text-[11px] font-mono text-slate-800 whitespace-pre-wrap break-all bg-cyber-100/60 border border-cyber-300/30 rounded p-2 max-h-40 overflow-auto">
          {evidence}
        </pre>
      )}
    </div>
  );
}
